import React, { useEffect, useState, useRef } from "react";
import * as XLSX from "xlsx";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress,
  Grid, IconButton, Tooltip, Snackbar, Alert, Box
} from "@mui/material";
import SaveAltTwoToneIcon from '@mui/icons-material/SaveAltTwoTone';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";

const ExcelViewer = ({ excelfile, width = 320 }) => {
  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [zoom, setZoom] = useState(1);
  const lref = useRef();

  useEffect(() => {
    const fetchAndParse = async () => {
      setLoading(true);
      try {
        const response = await fetch(excelfile.surl || excelfile.url || excelfile.cdn_url || excelfile.turl);
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Find the first non-empty row to use as headers
        let headerRowIdx = json.findIndex(row => Array.isArray(row) && row.some(cell => cell !== undefined && cell !== null && cell !== ""));
        let headers = [];
        let dataRows = [];
        if (headerRowIdx !== -1) {
          headers = json[headerRowIdx];
          dataRows = json.slice(headerRowIdx + 1);
        }

        setCols(headers || []);
        setRows(dataRows || []);
      } catch (e) {
        setCols([]);
        setRows([]);
      }
      setLoading(false);
    };
    fetchAndParse();
  }, [excelfile]);

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const onClickDownload = async () => {
    showSnackbar("Download started...", "info");
    try {
      const response = await fetch(excelfile.surl || excelfile.url || excelfile.cdn_url || excelfile.turl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = (excelfile.surl || excelfile.url || excelfile.cdn_url || excelfile.turl).split("/").pop();
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar("Download successful!", "success");
    } catch (error) {
      showSnackbar("Download failed. Please try again.", "error");
    }
  };

  const onClickOpen = () => {
    lref.current.click();
  };

  const zoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 2));
  const zoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.5));

  const buttonBaseStyle = {
    fontSize: '0.70rem',
    borderRadius: 2,
    color: 'white',
    minHeight: 28,
    minWidth: 28,
    padding: '2px 8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    lineHeight: 1.2,
  };

  const openButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: "#0ABAB5",
    "&:hover": {
      backgroundColor: "#0ab1ac",
    },
  };

  const downloadButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: "#4ea5f9",
    "&:hover": {
      backgroundColor: "#509af4",
    },
  };

  const zoomButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: "#607d8b",
    "&:hover": {
      backgroundColor: "#455a64",
    },
  };

  if (loading) return <CircularProgress size={32} />;

  if (!cols.length) return <div>No data found in Excel excelfile.</div>;

  return (
    <Grid container spacing={2} direction="column" alignItems="center">
      <Grid item style={{ maxWidth: width, width: "100%", overflowX: "auto", textAlign: "center" }}>
        <TableContainer component={Paper} sx={{ maxHeight: 320, maxWidth: width, margin: "0 auto" }}>
          <Table
            size="small"
            stickyHeader
            sx={{
              fontSize: `${zoom * 1}rem`,
              "& td, & th": {
                fontSize: `${zoom * 1}rem`,
                padding: `${zoom * 8}px ${zoom * 8}px`,
              },
            }}
          >
            <TableHead>
              <TableRow>
                {cols.map((col, idx) => (
                  <TableCell key={idx}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rIdx) => (
                <TableRow key={rIdx}>
                  {cols.map((_, cIdx) => (
                    <TableCell key={cIdx}>
                      {row[cIdx] !== undefined ? row[cIdx] : ""}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Grid
          item
          container
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ mt: 1 }}
        >
          <Grid item>
            <Tooltip title="Open" arrow>
              <IconButton size="small" onClick={onClickOpen} sx={openButtonStyle}>
                <FileOpenIcon fontSize="small" sx={{ marginRight: 0.5 }} />
                <span style={{ fontSize: '0.70rem' }}>Open</span>
              </IconButton>
            </Tooltip>
          </Grid>
          <a
            ref={lref}
            style={{ display: 'none' }}
            href={excelfile.surl || excelfile.url || excelfile.cdn_url || excelfile.turl}
            target="_blank"
            rel="noreferrer"
          />
          <Grid item>
            <Tooltip title="Download" arrow>
              <IconButton size="small" onClick={onClickDownload} sx={downloadButtonStyle}>
                <SaveAltTwoToneIcon fontSize="small" sx={{ marginRight: 0.3 }} />
                <span style={{ fontSize: '0.70rem' }}>Download</span>
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="Zoom In" arrow>
              <IconButton size="small" onClick={zoomIn} sx={zoomButtonStyle}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="Zoom Out" arrow>
              <IconButton size="small" onClick={zoomOut} sx={zoomButtonStyle}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default ExcelViewer;